/**
 * ChecklistEditSheet — SideSheet-based edit form for checklist items.
 *
 * Follows the AuditEditSheet.tsx pattern exactly:
 * - SideSheet compound component
 * - Framer Motion staggered field animations
 * - Cmd/Ctrl+Enter keyboard shortcut to save
 * - useApiMutation for backend updates
 */

import { useEffect, useState, useRef, useId } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AlignLeft, Loader2, PencilLine, StickyNote } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { api } from "convex/_generated/api";
import { SideSheet } from "@/components/ui";
import { Button, Textarea } from "@/components/ui/primitives";
import { useApiMutation } from "@/packages/features-shared";
import { useTranslation } from "@/packages/utils";
import type { UIChecklistItem, Id } from "@/packages/types";

// ─── Props ──────────────────────────────────────────────────────────────────

interface ChecklistEditSheetProps {
  open: boolean;
  onClose: () => void;
  item: UIChecklistItem | null;
  auditId: Id<"audits">;
}

// ─── Animation Variants ──────────────────────────────────────────────────────

const fieldVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.22, ease: "easeOut" as const },
  }),
};

// ─── Field Label ─────────────────────────────────────────────────────────────

function FieldLabel({
  children,
  required,
  icon: Icon,
}: {
  children: string;
  required?: boolean;
  icon?: LucideIcon;
}) {
  return (
    <div className="mb-1.5 flex items-center gap-1.5">
      {Icon && <Icon className="h-3.5 w-3.5 text-muted" />}
      <span className="text-[12px] font-medium text-secondary">
        {children}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </span>
    </div>
  );
}

// ─── Animated Error ───────────────────────────────────────────────────────────

function AnimatedError({ message }: { message?: string }) {
  return (
    <AnimatePresence>
      {message && (
        <motion.p
          key="error"
          initial={{ opacity: 0, height: 0, marginTop: 0 }}
          animate={{ opacity: 1, height: "auto", marginTop: 4 }}
          exit={{ opacity: 0, height: 0, marginTop: 0 }}
          transition={{ duration: 0.15 }}
          className="overflow-hidden text-[12px] text-destructive"
        >
          {message}
        </motion.p>
      )}
    </AnimatePresence>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export function ChecklistEditSheet({
  open,
  onClose,
  item,
}: ChecklistEditSheetProps) {
  const { t } = useTranslation(["audits", "common"]);
  const requirementId = useId();
  const notesId = useId();
  const requirementRef = useRef<HTMLTextAreaElement>(null);

  const [description, setDescription] = useState(item?.name ?? "");
  const [notes, setNotes] = useState(item?.notes ?? "");
  const [descriptionError, setDescriptionError] = useState("");

  const updateDetails = useApiMutation(
    api.checklist_mutations.updateChecklistItemDetails,
    {
      name: "update-checklist-item-details",
      successMessage: t("checklist.detail.requirementUpdated"),
    },
  );

  // Re-sync form when sheet opens or item changes
  useEffect(() => {
    if (open && item) {
      setDescription(item.name);
      setNotes(item.notes ?? "");
      setDescriptionError("");
      setTimeout(() => {
        requirementRef.current?.focus();
      }, 50);
    }
  }, [open, item]);

  const validate = (): boolean => {
    if (!description.trim()) {
      setDescriptionError(t("checklist.detail.requirementEmpty"));
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!item || !validate()) return;
    await updateDetails.mutateAsync({
      checklistItemId: item.id as Id<"checklistItems">,
      description: description.trim(),
      notes: notes.trim() || undefined,
    });
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      void handleSave();
    }
  };

  return (
    <SideSheet open={open} onClose={onClose}>
      <SideSheet.Header
        title={t("checklist.detail.editRequirement")}
        description={item?.name ?? ""}
        icon={<PencilLine size={18} />}
      />

      <SideSheet.Body>
        <div className="space-y-5 py-2">
          {/* Requirement field */}
          <motion.div
            variants={fieldVariants}
            initial="hidden"
            animate="visible"
            custom={0}
          >
            <FieldLabel required icon={AlignLeft}>
              {t("checklist.detail.requirementLabel")}
            </FieldLabel>
            <Textarea
              ref={requirementRef}
              id={requirementId}
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                if (e.target.value.trim()) setDescriptionError("");
              }}
              onKeyDown={handleKeyDown}
              placeholder={t("checklist.detail.requirementPlaceholder")}
              rows={6}
            />
            <AnimatedError message={descriptionError} />
          </motion.div>

          {/* Notes field */}
          <motion.div
            variants={fieldVariants}
            initial="hidden"
            animate="visible"
            custom={1}
          >
            <FieldLabel icon={StickyNote}>
              {t("checklist.detail.notesLabel")}
            </FieldLabel>
            <Textarea
              id={notesId}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t("checklist.detail.contextPlaceholder")}
              rows={4}
            />
          </motion.div>
        </div>
      </SideSheet.Body>

      <SideSheet.Footer>
        <Button variant="secondary" onClick={onClose}>
          {t("common:buttons.cancel")}
        </Button>
        <Button
          variant="primary"
          onClick={() => void handleSave()}
          disabled={updateDetails.isPending}
        >
          {updateDetails.isPending ? (
            <span className="flex items-center gap-1.5">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {t("edit.actions.saving")}
            </span>
          ) : (
            t("checklist.detail.saveChanges")
          )}
        </Button>
      </SideSheet.Footer>
    </SideSheet>
  );
}
